import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card, Form, Input, Button, Space, Typography, Alert, Spin, Row, Col,
  Upload, message, Avatar
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, UploadOutlined,
  ShopOutlined, EnvironmentOutlined, BankOutlined, MailOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import inquiryService from '../../../services/inquiryService';
import BackButton from '../../../components/BackButton';
import CityAutocomplete from '../../../components/CityAutocomplete';

const { Title, Text } = Typography;

const PartnerProfileEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTaxRequest, setSendingTaxRequest] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get('/partners/me/');
      setProfile(response.data);
      form.setFieldsValue({
        store_name: response.data.store_name,
        tax_number: response.data.tax_number,
        address: response.data.address,
        city: response.data.city,
        postal_code: response.data.postal_code,
        country: response.data.country,
      });
      if (response.data.logo?.thumbnail) {
        setLogoPreview(response.data.logo.thumbnail);
      }
    } catch (err) {
      setError(t('partner.profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (info) => {
    const file = info.file;
    if (file) {
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTaxNumberChangeRequest = async () => {
    setSendingTaxRequest(true);
    try {
      await inquiryService.createInquiry({
        inquiry_type: 'GENERAL',
        subject: t('partner.profile.taxChangeRequestSubject'),
        message: t('partner.profile.taxChangeRequestMessage', {
          storeName: profile?.store_name,
          currentTaxNumber: profile?.tax_number
        })
      });
      message.success(t('partner.profile.taxChangeRequestSent'));
    } catch (err) {
      message.error(t('partner.profile.taxChangeRequestFailed'));
    } finally {
      setSendingTaxRequest(false);
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    setError(null);

    try {
      const formData = new FormData();

      // Add text fields (exclude tax_number as it can't be changed directly)
      Object.keys(values).forEach(key => {
        if (key !== 'tax_number' && values[key] !== null && values[key] !== undefined) {
          formData.append(key, values[key]);
        }
      });

      // Add logo if changed
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      await api.patch('/partners/me/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success(t('partner.profile.updateSuccess'));
      navigate('/partner/dashboard');
    } catch (err) {
      if (err.response?.data) {
        const errors = err.response.data;
        if (typeof errors === 'object') {
          const firstError = Object.values(errors)[0];
          setError(Array.isArray(firstError) ? firstError[0] : firstError);
        } else {
          setError(t('partner.profile.updateFailed'));
        }
      } else {
        setError(t('partner.profile.updateFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <BackButton to="/partner/dashboard" label={t('partner.backToDashboard')} />

      <Title level={2} style={{ marginBottom: 24 }}>
        <BankOutlined /> {t('partner.profile.title')}
      </Title>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={24}>
          {/* Logo Section */}
          <Col xs={24} md={8}>
            <Card title={t('partner.profile.logo')}>
              <div style={{ textAlign: 'center' }}>
                <Avatar
                  size={150}
                  src={logoPreview}
                  icon={<ShopOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <br />
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleLogoChange({ file });
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>
                    {t('partner.profile.uploadLogo')}
                  </Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  {t('partner.profile.logoHelp')}
                </Text>
              </div>
            </Card>
          </Col>

          {/* Business Info */}
          <Col xs={24} md={16}>
            <Card title={t('partner.profile.businessInfo')}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="store_name"
                    label={t('partner.profile.storeName')}
                    rules={[{ required: true, message: t('partner.profile.storeNameRequired') }]}
                  >
                    <Input prefix={<ShopOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tax_number"
                    label={t('partner.profile.taxNumber')}
                  >
                    <Input disabled />
                  </Form.Item>
                  <Button
                    type="link"
                    icon={<MailOutlined />}
                    onClick={handleTaxNumberChangeRequest}
                    loading={sendingTaxRequest}
                    style={{ marginTop: -16, padding: 0 }}
                  >
                    {t('partner.profile.requestTaxChange')}
                  </Button>
                </Col>
              </Row>
            </Card>

            <Card title={t('partner.profile.address')} style={{ marginTop: 16 }}>
              <Form.Item
                name="address"
                label={t('partner.profile.streetAddress')}
                rules={[{ required: true, message: t('partner.profile.addressRequired') }]}
              >
                <Input prefix={<EnvironmentOutlined />} />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="city"
                    label={t('common.city')}
                    rules={[{ required: true, message: t('partner.profile.cityRequired') }]}
                  >
                    <CityAutocomplete placeholder={t('common.city')} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="postal_code"
                    label={t('common.postalCode')}
                    rules={[{ required: true, message: t('partner.profile.postalCodeRequired') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="country"
                    label={t('common.country')}
                    rules={[{ required: true, message: t('partner.profile.countryRequired') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={() => navigate('/partner/dashboard')}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {t('common.save')}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default PartnerProfileEdit;
